
import React from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiCheckCircle, FiShare, FiClock, FiArrowRight, FiBriefcase, FiCalendar, FiVideo, FiClipboard, FiEye, FiInfo, FiUser, FiRefreshCw, FiFileText, FiGift, FiX, FiTrendingUp, FiUsers, FiVolume2, FiStar
} from "react-icons/fi";
import { API_BASE_URL } from "../utils/config";
import axios from "axios";
import "./Dashboard.css";
import "./EmployeeDashboard.css";

const getStatusBadge = (status) => {
  switch (status) {
    case "Hired":
    case "Selected":
      return "bg-green-100 text-green-700";
    case "Rejected":
      return "bg-red-100 text-red-700";
    case "Interview":
      return "bg-blue-100 text-blue-700";
    case "Shortlisted":
      return "bg-indigo-100 text-indigo-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const CandidateDashboard = () => {
  const { profile, appliedJobs = [], allJobs = [], personalDocs } = useOutletContext();
  const navigate = useNavigate();

  const [notifications, setNotifications] = React.useState([]);
  const [showProfilePrompt, setShowProfilePrompt] = React.useState(false);
  const [showCoinPopup, setShowCoinPopup] = React.useState(false);
  const [isVoicePlaying, setIsVoicePlaying] = React.useState(false);

  // All coin earning tips in one popup
  const coinTips = [
    { action: "Apply for a Job", reward: "20 Coins", icon: FiBriefcase, color: "green" },
    { action: "Complete Assessment", reward: "10 Coins", icon: FiCheckCircle, color: "blue" },
    { action: "Attend Interview", reward: "50 Coins", icon: FiVideo, color: "purple" },
    { action: "Get Selected/Hired", reward: "100 Coins", icon: FiStar, color: "amber" },
    { action: "Refer a Friend", reward: "200 Coins", icon: FiUsers, color: "indigo" },
    { action: "Top Performer", reward: "100 Coins", icon: FiTrendingUp, color: "emerald" },
    { action: "Complete Profile", reward: "30 Coins", icon: FiUser, color: "orange" },
    { action: "Share Job Posting", reward: "15 Coins", icon: FiShare, color: "rose" },
  ];

  // Play voice message function (Girl's voice)
  const playVoiceMessage = (message) => {
    if (!window.speechSynthesis) {
      console.log("Speech synthesis not supported");
      return;
    }

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(message);
    
    // Voice settings for female voice
    utterance.rate = 0.9;
    utterance.pitch = 1.2;
    utterance.volume = 1;
    
    // Function to find and set female voice
    const setFemaleVoice = () => {
      const voices = window.speechSynthesis.getVoices();
      const femaleVoice = voices.find(voice => 
        voice.name.includes("Google UK English Female") ||
        voice.name.includes("Samantha") ||
        voice.name.includes("Victoria") ||
        voice.name.includes("Susan") ||
        voice.name.includes("Microsoft Zira") ||
        voice.name.includes("Google US English Female") ||
        voice.name.toLowerCase().includes("female")
      );
      
      if (femaleVoice) {
        utterance.voice = femaleVoice;
      }
      window.speechSynthesis.speak(utterance);
    };
    
    // Check if voices are already loaded
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) {
      setFemaleVoice();
    } else {
      window.speechSynthesis.onvoiceschanged = () => {
        setFemaleVoice();
      };
    }
    
    setIsVoicePlaying(true);
    
    utterance.onend = () => {
      setIsVoicePlaying(false);
    };
    
    utterance.onerror = () => {
      setIsVoicePlaying(false);
    };
  };

  // Random messages for voice
  const voiceMessages = [
    "Hey! Did you know you can earn coins by applying for jobs? Check out how!",
    "Want to earn rewards? Complete activities and get coins!",
    "Earn up to 525 coins by completing simple tasks. Click to learn more!",
    "Your next reward is waiting! Apply for jobs and earn coins today!",
    "Don't miss out! Complete your profile and earn 30 coins instantly!"
  ];

  React.useEffect(() => {
    if (personalDocs) {
      const docs = personalDocs.documents || {};
      const criticalDocs = ["aadharCard", "panCard", "tenthCertificate", "twelfthCertificate"];
      const missingDocs = criticalDocs.filter(type => !docs[type]?.url);

      const hasBank = !!docs.bankDetails?.accountNumber;
      const hasEmergency = !!docs.emergencyContact1?.phone;

      if (missingDocs.length > 0 || !hasBank || !hasEmergency) {
        const hasPrompted = sessionStorage.getItem("profilePrompted");
        if (!hasPrompted) {
          setShowProfilePrompt(true);
          sessionStorage.setItem("profilePrompted", "true");
        }
      }
    }
  }, [personalDocs]);

  React.useEffect(() => {
    if (profile?._id) {
      fetchNotifications();
    }
  }, [profile?._id]);

  // Show popup every 1 minute (60 seconds) with voice
  React.useEffect(() => {
    if (!profile?._id) return;

    const showPopupWithVoice = () => {
      setShowCoinPopup(true);
      // Play random voice message
      const randomMessage = voiceMessages[Math.floor(Math.random() * voiceMessages.length)];
      playVoiceMessage(randomMessage);
      
      // Auto hide after 1 minute (60 seconds)
      setTimeout(() => {
        setShowCoinPopup(false);
      }, 60000);
    };

    // Show first popup after 5 seconds
    const firstTimer = setTimeout(() => {
      showPopupWithVoice();
    }, 5000);

    // Then show every 1 minute (60000 ms)
    const interval = setInterval(() => {
      showPopupWithVoice();
    }, 60000);

    return () => {
      clearTimeout(firstTimer);
      clearInterval(interval);
    };
  }, [profile?._id]);

  const fetchNotifications = async () => {
    try {
      const recipientId = profile?._id || profile?.candidateId;
      if (!recipientId) return;
      const res = await axios.get(`${API_BASE_URL}/medical-certificates/notifications/${recipientId}`);
      if (res.data?.success) {
        setNotifications(res.data.data.filter(n => !n.read));
      }
    } catch (err) {
      setNotifications([]);
    }
  };

  const totalApps = appliedJobs.length;
  const activeApps = appliedJobs.filter(a => !["Rejected", "Withdrawn"].includes(a.status)).length;
  const hiredApps = appliedJobs.filter(a => ["Hired", "Selected"].includes(a.status)).length;

  const pendingInterviews = appliedJobs.filter(a =>
    (a.interviewStatus === "Invited" || a.interviewStatus === "Rescheduled") &&
    a.candidateInterviewStatus === "Pending"
  );

  const recentActivity = [...appliedJobs]
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    .slice(0, 10);

  return (
    <div className="emp-dash">
      <main className="p-4 sm:p-6 lg:p-8">
        {/* Dashboard Header */}
        <div className="emp-dash__header">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="emp-dash__greeting">
                Candidate <span>Dashboard</span>
              </h1>
              <p className="emp-dash__subtitle">
                Track your applications, interviews, and career progress
              </p>
            </div>
            <div className="emp-dash__date-pill">
              <FiClipboard />
              <span>
                {new Date().toLocaleDateString("en-US", {
                  weekday: "short",
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Profile Header Card */}
        {/* <div className="emp-dash__card mb-6">
          <div className="flex flex-col sm:flex-wrap sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 w-full sm:w-auto">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-green-500 to-blue-600 flex items-center justify-center text-white shadow-md shrink-0">
                <FiUser size={20} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  Welcome, {profile?.name || "Candidate"}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="flex items-center gap-1.5 bg-green-50 text-green-600 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest border border-green-100">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block"></span>
                    Profile Active
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate("/all-jobs")}
              className="w-full sm:w-auto h-9 px-5 text-xs font-bold bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-xl hover:opacity-90 transition flex items-center justify-center gap-2 shadow-md"
            >
              <FiBriefcase className="text-xs" /> Browse Jobs
            </button>
          </div>
        </div> */}

        {/* Stats Row */}
        <div className="emp-dash__stats grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="emp-dash__stat" onClick={() => navigate("/all-jobs")}>
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Open Roles</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--indigo">
                <FiBriefcase />
              </div>
            </div>
            <div className="emp-dash__stat-value">{allJobs.length}</div>
            <div className="emp-dash__stat-meta">available positions</div>
          </div>
          <div className="emp-dash__stat" onClick={() => navigate("/applied-jobs")}>
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Applied</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--violet">
                <FiClipboard />
              </div>
            </div>
            <div className="emp-dash__stat-value">{totalApps}</div>
            <div className="emp-dash__stat-meta">applications submitted</div>
          </div>
          <div className="emp-dash__stat" onClick={() => navigate("/applied-jobs")}>
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Active</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--emerald">
                <FiCalendar />
              </div>
            </div>
            <div className="emp-dash__stat-value">{activeApps}</div>
            <div className="emp-dash__stat-meta">active applications</div>
          </div>
          <div className="emp-dash__stat" onClick={() => navigate("/interview")}>
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Interviews</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rose">
                <FiVideo />
              </div>
            </div>
            <div className="emp-dash__stat-value">{pendingInterviews.length}</div>
            <div className="emp-dash__stat-meta">pending interviews</div>
          </div>
        </div>

        {/* Compliance Alerts */}
        <AnimatePresence>
          {notifications.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <div className="emp-dash__card">
                <div className="emp-dash__card-header">
                  <h3 className="emp-dash__card-title flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-rose-500 animate-ping"></span>
                    Urgent Compliance Alerts
                  </h3>
                </div>
                <div className="emp-dash__card-body space-y-3">
                  {notifications.map((note) => (
                    <div
                      key={note._id}
                      className="bg-white border-l-4 border-rose-500 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 group hover:shadow-md transition-all"
                    >
                      <div className="flex items-center gap-4 w-full sm:w-auto">
                        <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center text-rose-500 shrink-0">
                          <FiFileText size={18} />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-gray-800">{note.message}</p>
                          <p className="text-[10px] text-gray-400 font-medium mt-0.5 uppercase tracking-wider">Received on {new Date(note.createdAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate("/medical-certificate")}
                        className="w-full sm:w-auto px-4 py-2 bg-rose-500 text-white rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-rose-600 transition-all flex items-center justify-center gap-2 shadow-sm"
                      >
                        Action <FiArrowRight size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pending Interview Actions */}
        <AnimatePresence>
          {pendingInterviews.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 overflow-hidden"
            >
              <div className="emp-dash__card">
                <div className="emp-dash__card-header">
                  <h3 className="emp-dash__card-title flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping inline-block"></span>
                    Interview Actions Required
                  </h3>
                  <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-3 py-0.5 rounded-full border border-amber-200">
                    Response Needed
                  </span>
                </div>
                <div className="emp-dash__table-wrap overflow-x-auto">
                  <table className="emp-dash__table min-w-[600px]">
                    <thead>
                      <tr>
                        <th className="py-3 px-4 text-center font-bold">Position</th>
                        <th className="py-3 px-4 text-center font-bold">Scheduled Time</th>
                        <th className="py-3 px-4 text-center font-bold">Mode</th>
                        <th className="py-3 px-4 text-center font-bold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingInterviews.map((app) => (
                        <tr key={app._id} className="border-b hover:bg-amber-50/30 transition-colors">
                          <td className="px-4 py-2 text-center">
                            <div className="text-xs font-bold text-gray-800">{app.jobId?.role || "Position Update"}</div>
                            <div className="text-[9px] text-gray-400 uppercase tracking-wider">Ref: {app._id.slice(-6).toUpperCase()}</div>
                          </td>
                          <td className="px-4 py-2 text-center">
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-600 bg-gray-100 px-2 py-1 rounded">
                              <FiClock className="text-amber-500 text-[9px]" />
                              {new Date(app.interviewTime).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-center">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${app.interviewMode?.toLowerCase() === "online" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                              {app.interviewMode || "Online"}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-center">
                            <button
                              onClick={() => navigate("/interview")}
                              className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded text-[10px] font-bold hover:from-amber-600 hover:to-orange-600 transition flex items-center gap-1 mx-auto"
                            >
                              View Details <FiArrowRight className="text-[9px]" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Application Status Tracker */}
        <div className="emp-dash__card mb-6">
          <div className="emp-dash__card-header">
            <h3 className="emp-dash__card-title flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-600 inline-block"></span>
              Application Status Tracker
            </h3>
            <button
              onClick={() => navigate("/applied-jobs")}
              className="h-7 px-3 text-[10px] font-bold text-blue-600 hover:bg-blue-50 border border-blue-200 rounded transition"
            >
              View All →
            </button>
          </div>
          {recentActivity.length > 0 ? (
            <div className="emp-dash__table-wrap overflow-x-auto">
              <table className="emp-dash__table min-w-[800px]">
                <thead>
                  <tr>
                    <th className="py-3 px-4 text-center font-bold">Position</th>
                    <th className="py-3 px-4 text-center font-bold">Status</th>
                    <th className="py-3 px-4 text-center font-bold hidden md:table-cell">Last Updated</th>
                    <th className="py-3 px-4 text-center font-bold">Progress</th>
                    <th className="py-3 px-4 text-center font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {recentActivity.map((activity) => (
                    <tr key={activity._id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2 font-medium text-center">
                        <div className="text-xs font-bold text-gray-800">{activity.jobId?.role || "Position"}</div>
                        {activity.jobId?.location && (
                          <div className="text-[9px] text-gray-400 uppercase tracking-wider mt-0.5 hidden md:block">{activity.jobId.location}</div>
                        )}
                        <div className="text-[9px] text-gray-400 uppercase tracking-wider mt-0.5 block md:hidden">
                          {new Date(activity.updatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${getStatusBadge(activity.status)}`}>
                          {activity.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center hidden md:table-cell">
                        <span className="text-[10px] font-medium text-gray-500">
                          {new Date(activity.updatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center w-40">
                        <div className="flex items-center gap-2 justify-center font-bold">
                          <div className="h-1.5 flex-1 bg-gray-100 rounded-full overflow-hidden hidden md:block">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{
                                width: (() => {
                                  if (activity.status === "Rejected" || activity.status === "Selected" || activity.status === "Hired") {
                                    return "100%";
                                  }
                                  if (activity.status === "Pending") {
                                    return "50%";
                                  }
                                  // Check if assessment is not done
                                  if (activity.jobId && (activity.jobId.assessmentId || (activity.jobId.assessmentIds && activity.jobId.assessmentIds.length > 0))) {
                                    if (!activity.assessmentResults || activity.assessmentResults.length === 0) {
                                      return "20%";
                                    }
                                  }
                                  return "20%";
                                })()
                              }}
                              transition={{ delay: 0.3, duration: 0.8 }}
                              className={`h-full rounded-full ${
                                activity.status === "Rejected" 
                                  ? "bg-gradient-to-r from-red-500 to-red-600"
                                  : activity.status === "Selected" || activity.status === "Hired"
                                    ? "bg-gradient-to-r from-green-500 to-emerald-600"
                                    : "bg-gradient-to-r from-blue-500 to-indigo-600"
                              }`}
                            />
                          </div>
                          <span className="text-[10px] md:text-[9px] text-blue-600 md:text-gray-400 w-auto md:w-8 text-center md:text-right shrink-0">
                            {(() => {
                              if (activity.status === "Rejected" || activity.status === "Selected" || activity.status === "Hired") {
                                return "100%";
                              }
                              if (activity.status === "Pending") {
                                return "50%";
                              }
                              // Check if assessment is not done
                              if (activity.jobId && (activity.jobId.assessmentId || (activity.jobId.assessmentIds && activity.jobId.assessmentIds.length > 0))) {
                                if (!activity.assessmentResults || activity.assessmentResults.length === 0) {
                                  return "20%";
                                }
                              }
                              return "20%";
                            })()}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        {activity.jobId && (activity.jobId.assessmentId || (activity.jobId.assessmentIds && activity.jobId.assessmentIds.length > 0)) ? (() => {
                          const allAssessmentIds = [];
                          if (activity.jobId.assessmentIds && activity.jobId.assessmentIds.length > 0) {
                            activity.jobId.assessmentIds.forEach(a => {
                              const id = a?._id || a;
                              if (id) allAssessmentIds.push(String(id));
                            });
                          } else if (activity.jobId.assessmentId) {
                            const id = activity.jobId.assessmentId?._id || activity.jobId.assessmentId;
                            if (id) allAssessmentIds.push(String(id));
                          }

                          const completedIds = (activity.assessmentResults || []).map(r =>
                            String(r.quizId?._id || r.quizId)
                          );

                          const nextPendingId = allAssessmentIds.find(id => !completedIds.includes(id));
                          const doneCount = completedIds.filter(id => allAssessmentIds.includes(id)).length;
                          const totalCount = allAssessmentIds.length;
                          const allDone = !nextPendingId && totalCount > 0;

                          if (allDone) {
                            return (
                              <div className="flex flex-col items-center">
                                <span className="inline-flex items-center gap-1.5 text-[10px] font-black text-emerald-600 whitespace-nowrap">
                                  <FiCheckCircle className="text-[11px]" /> ALL VERIFIED
                                </span>
                                {doneCount > 1 && (
                                  <span className="text-[9px] font-bold text-gray-400">{doneCount}/{totalCount} tests</span>
                                )}
                              </div>
                            );
                          }

                          if (nextPendingId) {
                            const nextIdx = allAssessmentIds.indexOf(nextPendingId) + 1;
                            return (
                              <div className="flex flex-col items-center gap-0.5">
                                {doneCount > 0 && (
                                  <span className="text-[9px] font-bold text-emerald-500">{doneCount}/{totalCount} Done ✓</span>
                                )}
                                <button
                                  onClick={() => navigate(`/take-assessment/${activity.jobId._id}/${activity._id}/${nextPendingId}`)}
                                  className="inline-flex items-center gap-1.5 bg-transparent text-blue-600 text-[10px] font-bold uppercase tracking-widest hover:text-blue-800 transition-all group/test whitespace-nowrap"
                                >
                                  <FiRefreshCw size={14} className="group-hover/test:rotate-180 transition-transform duration-500" />
                                  Test {nextIdx}{totalCount > 1 ? ` of ${totalCount}` : ""}
                                </button>
                              </div>
                            );
                          }

                          return null;
                        })() : (
                          <button
                            onClick={() => navigate("/applied-jobs")}
                            className="w-9 h-9 bg-transparent text-indigo-600 hover:text-indigo-800 transition-all active:scale-95 group flex items-center justify-center mx-auto"
                            title="View Details"
                          >
                            <FiEye className="text-xl transition-transform group-hover:scale-110" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-10 text-center">
              <div className="flex flex-col items-center justify-center gap-3">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-300">
                  <FiBriefcase size={32} />
                </div>
                <span className="text-sm font-medium text-gray-500">No applications yet</span>
                <button
                  onClick={() => navigate("/all-jobs")}
                  className="px-5 py-2 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-lg text-xs font-bold hover:from-green-600 hover:to-blue-700 transition shadow-md"
                >
                  Browse Jobs
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em] py-4">
          <FiInfo className="text-blue-400" />
          Timely Health Recruitment Portal • {new Date().getFullYear()}
        </div>
      </main>

      {/* Profile Completion Prompt */}
      <AnimatePresence>
        {showProfilePrompt && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white w-full max-w-md sm:max-w-lg rounded-[2.5rem] shadow-2xl p-6 sm:p-10 border border-gray-100 relative text-center"
            >
              <div className="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-inner border border-indigo-100/50">
                <FiUser size={32} />
              </div>
              <h2 className="text-xl font-black text-gray-900 tracking-tight uppercase tracking-widest mb-2 text-center">Complete Your Profile</h2>
              <p className="text-sm text-gray-500 font-medium leading-relaxed mb-8">
                Your application has been received! To move forward with the hiring process, please complete your profile by uploading your documents and details.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowProfilePrompt(false);
                    navigate("/candidate-personal-documents");
                  }}
                  className="w-full py-4 bg-gradient-to-r from-indigo-600 to-blue-700 text-white rounded-2xl text-[10px] font-bold uppercase tracking-[0.2em] shadow-lg shadow-indigo-200 hover:scale-[1.02] transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  Complete Profile Now <FiArrowRight size={10} />
                </button>
                <button
                  onClick={() => setShowProfilePrompt(false)}
                  className="w-full py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors"
                >
                  I'll do it later
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* How to Earn Coins Popup with Voice */}
      <AnimatePresence>
        {showCoinPopup && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white w-full max-w-2xl sm:max-w-3xl rounded-[2rem] shadow-2xl overflow-hidden border border-gray-100 relative"
            >
              {/* Voice Playing Indicator */}
              {isVoicePlaying && (
                <div className="absolute top-4 left-4 z-20">
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                    className="flex items-center gap-1 bg-green-500 text-white px-2 py-1 rounded-full text-[8px] font-bold"
                  >
                    <FiVolume2 size={10} />
                    <span>Voice Message Playing...</span>
                  </motion.div>
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={() => setShowCoinPopup(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-all z-10"
              >
                <FiX size={14} />
              </button>

              {/* Header */}
              <div className="bg-gradient-to-r from-green-500 to-blue-600 px-8 py-6 text-center">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <motion.div
                    animate={{ 
                      scale: [1, 1.2, 1],
                      rotate: [0, 10, -10, 0]
                    }}
                    transition={{ duration: 0.8, repeat: Infinity, repeatDelay: 2 }}
                  >
                    <FiStar className="text-yellow-300 text-3xl" />
                  </motion.div>
                  <FiGift className="text-yellow-300 text-2xl" />
                </div>
                <h2 className="text-xl font-black text-white uppercase tracking-wider">Earn Coins & Rewards!</h2>
                <p className="text-white/80 text-xs mt-1">Complete activities and earn coins to unlock benefits</p>
              </div>

              {/* Content */}
              <div className="p-8 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {coinTips.map((tip, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ scale: 1.02, x: 5 }}
                      className="flex items-center gap-3 p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 cursor-pointer hover:shadow-md transition-all"
                      onClick={() => {
                        if (tip.action === "Apply for a Job") navigate("/all-jobs");
                        else if (tip.action === "Complete Assessment" || tip.action === "Attend Interview") navigate("/applied-jobs");
                        else if (tip.action === "Complete Profile") navigate("/candidate-personal-documents");
                        else if (tip.action === "Refer a Friend") {
                          navigator.clipboard.writeText(window.location.href);
                          alert("Referral link copied!");
                        }
                        setShowCoinPopup(false);
                      }}
                    >
                      <div className={`w-10 h-10 rounded-xl bg-${tip.color}-100 text-${tip.color}-600 flex items-center justify-center`}>
                        <tip.icon className="text-sm" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-bold text-gray-800">{tip.action}</p>
                        <p className="text-[10px] font-bold text-green-600">{tip.reward}</p>
                      </div>
                      <FiArrowRight className="text-gray-300 text-xs" />
                    </motion.div>
                  ))}
                </div>

                {/* Total Earning Potential */}
                <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Total Earning Potential</p>
                      <p className="text-lg font-black text-amber-600">525+ Coins</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                      <FiStar className="text-amber-600 text-xl" />
                    </div>
                  </div>
                </div>

                {/* Voice Message Text */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-4 p-3 bg-green-50 rounded-xl border border-green-100"
                >
                  <p className="text-[10px] text-green-600 font-medium flex items-center justify-center gap-2">
                    <FiVolume2 className="text-green-500" />
                    <span>🎤 Voice Tip: Complete activities regularly to maximize your coin earnings!</span>
                  </p>
                </motion.div>
              </div>

              {/* Footer */}
              <div className="px-8 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => setShowCoinPopup(false)}
                  className="px-6 py-2 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-xl text-[10px] font-bold uppercase tracking-wider hover:opacity-90 transition"
                >
                  Got it!
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CandidateDashboard;